import { useMutation, useQueryClient } from "react-query";

import axios, { AxiosError } from "axios";

import {
    ApiConversation,
    ApiFile,
    ApiMessage,
    ApiMessageTypes,
    ApiFragmentNode,
} from "../shared/interfaces";
import { PrimeQueryKeys } from "../shared/constants";

import { FileUploadProps } from "../components/FileUpload";
import { AnalyticsEvents, analyticsEvent } from "../util/analytics";

const fileUploadPropsToApiMessage = (file: FileUploadProps): ApiFile => {
    return {
        id: file.id,
        createdAt: file.createdAt,
        fileName: file.name,
        fileSize: file.size,
        fileUrl: "NA",
    };
};

interface SendMessageArgs {
    conversationId: string;
    message: string;
    messageType: string;
    files: FileUploadProps[];
    fragmentNode?: ApiFragmentNode;
}

export default function useSendMessageMutation() {
    const queryClient = useQueryClient();
    return useMutation<ApiMessage[], ApiMessage, SendMessageArgs>({
        mutationFn: async (args) => {
            const { conversationId, message, files, messageType, fragmentNode } = args;
            const resp = await axios.post("/api/message", {
                files: files.map((f) => f.id),
                conversationId: conversationId,
                message,
                messageType,
                fragmentNode,
            });

            analyticsEvent(AnalyticsEvents.SEND_MESSAGE);

            return resp.data;
        },
        onMutate: async (args) => {
            const { conversationId, message, messageType, files, fragmentNode } = args;

            const optimistic: ApiMessage = {
                id: Date.now().toString(),
                createdAt: new Date(),
                references: null,
                parentId: null,
                conversationId: conversationId,
                user: { id: "na", email: "na", name: "You" },
                files: files.map(fileUploadPropsToApiMessage),
                message,
                messageType,
                fragmentNode,
            };

            queryClient.setQueryData(
                PrimeQueryKeys.conversation(conversationId),
                (old: ApiConversation | undefined): ApiConversation => {
                    const newMessages = [...(old?.messages || []), optimistic];
                    return Object.assign({}, old, { messages: newMessages });
                },
            );

            return optimistic.id;
        },
        onSuccess: (result, variables, context) => {
            queryClient.setQueryData(
                PrimeQueryKeys.conversation(variables.conversationId),
                (old: ApiConversation | undefined): ApiConversation => {
                    const filterIds = [context, ...result.map((m) => m.id)];
                    const filteredMessages = (old?.messages ?? []).filter(
                        (m) => !filterIds.includes(m.id),
                    );
                    const newMessages = [...filteredMessages, ...result];
                    return Object.assign({}, old, { messages: newMessages });
                },
            );
        },
        onError: (error, variables) => {
            const er = error as unknown as AxiosError;
            const result: ApiMessage = {
                id: Date.now().toString(),
                createdAt: new Date(),
                message: JSON.stringify(er.response!.data, null, 2),
                messageType: ApiMessageTypes.Error,
                references: null,
                parentId: null,
                conversationId: variables.conversationId,
                user: {
                    id: "na",
                    email: "na",
                    name: "Marvin",
                },
                files: [],
            };

            queryClient.setQueryData(
                PrimeQueryKeys.conversation(variables.conversationId),
                (old: ApiConversation | undefined): ApiConversation => {
                    const newMessages = [...(old?.messages ?? []), ...[result]];

                    return Object.assign({}, old, { messages: newMessages });
                },
            );
        },
    });
}
